/* eslint-disable @typescript-eslint/prefer-optional-chain */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable tailwindcss/no-custom-classname */
/* eslint-disable lingui/no-unlocalized-strings */
import { t } from "@lingui/macro";
import { ResumeDto } from "@reactive-resume/dto";
import { pageSizeMap } from "@reactive-resume/utils";
import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { LoaderFunction, redirect } from "react-router-dom";

import DifyChat from "@/client/components/dify-chat";
import { queryClient } from "@/client/libs/query-client";
import { findResumeById } from "@/client/services/resume";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";

type PageFormat = keyof typeof pageSizeMap;

export const BuilderPage = () => {
  const resume = useResumeStore((state) => state.resume);
  const title = useResumeStore((state) => state.resume.title);
  const leftHandle = useBuilderStore((state) => state.panel.left.handle);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const format = resume.data.metadata.page.format as PageFormat;
  const [scale, setScale] = useState(1);

  // 计算适合容器的缩放比例
  const calculateScale = useCallback(() => {
    if (!containerRef.current || !frameRef.current) return;

    const containerWidth = containerRef.current.clientWidth;
    const pageWidth = pageSizeMap[format].width;

    // 计算适合宽度的缩放，保留20px的边距
    let newScale = (containerWidth - 120) / (pageWidth * 3.78);

    // 限制缩放范围，避免过大或过小
    newScale = Math.min(Math.max(newScale, 0.5), 1.2);

    // 检查是否有手动设置的缩放值
    const savedZoomLevel = localStorage.getItem("resume-zoom-level");
    if (savedZoomLevel) {
      // 如果有手动设置的缩放级别，使用预设的缩放系数
      const zoomLevels = [1, 0.85, 0.7];
      const level = Number.parseInt(savedZoomLevel, 10);
      if (level >= 0 && level < zoomLevels.length) {
        newScale = zoomLevels[level];
      }
    }

    setScale(newScale);

    // 更新container的宽度样式
    const frameContainer = frameRef.current.parentElement;
    if (frameContainer) {
      frameContainer.style.width = `${pageWidth * 3.78}px`;
      frameContainer.style.transform = `scale(${newScale})`;
      frameContainer.style.transformOrigin = "top center";

      // 确保容器高度足够显示所有内容
      if (frameRef.current.offsetHeight) {
        frameContainer.style.minHeight = `${frameRef.current.offsetHeight * newScale}px`;
      }
    }
  }, [format]);

  // 处理缩放操作
  const handleZoomIn = useCallback(() => {
    setScale((prevScale) => {
      const newScale = Math.min(prevScale + 0.1, 1.5);

      // 直接调整iframe容器的缩放
      const frameContainer = frameRef.current?.parentElement;
      if (frameContainer) {
        frameContainer.style.transform = `scale(${newScale})`;

        // 确保长页面也能正确显示缩放后的内容
        const height = frameRef.current.offsetHeight || 1000;
        frameContainer.style.minHeight = `${height * newScale}px`;
      }

      // 同时发送消息到iframe（针对使用TransformWrapper的布局模式）
      frameRef.current?.contentWindow?.postMessage({ type: "ZOOM_IN" }, "*");

      // 确保缩放操作有明显的视觉反馈
      setTimeout(() => {
        // 模拟窗口大小变化，触发重新计算布局
        window.dispatchEvent(new Event("resize"));
      }, 50);

      return newScale;
    });
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((prevScale) => {
      const newScale = Math.max(prevScale - 0.15, 0.5);

      // 直接调整iframe容器的缩放
      const frameContainer = frameRef.current?.parentElement;
      if (frameContainer) {
        frameContainer.style.transform = `scale(${newScale})`;

        // 确保长页面也能正确显示缩放后的内容
        const height = frameRef.current.offsetHeight || 1000;
        frameContainer.style.minHeight = `${height * newScale}px`;
      }

      // 同时发送消息到iframe（针对使用TransformWrapper的布局模式）
      frameRef.current?.contentWindow?.postMessage({ type: "ZOOM_OUT" }, "*");

      // 确保缩放操作有明显的视觉反馈
      setTimeout(() => {
        // 模拟窗口大小变化，触发重新计算布局
        window.dispatchEvent(new Event("resize"));
      }, 50);

      return newScale;
    });
  }, []);

  const handleResetView = useCallback(() => {
    // 重新计算适合容器的缩放
    calculateScale();

    // 同时发送消息到iframe（针对使用TransformWrapper的布局模式）
    frameRef.current?.contentWindow?.postMessage({ type: "RESET_VIEW" }, "*");
  }, [calculateScale]);

  const handleCenterView = useCallback(() => {
    if (containerRef.current) {
      // 滚动到顶部以实现居中效果
      containerRef.current.scrollTop = 0;

      // 同时发送消息到iframe（针对使用TransformWrapper的布局模式）
      frameRef.current?.contentWindow?.postMessage({ type: "CENTER_VIEW" }, "*");
    }
  }, []);

  // 发送垂直布局消息
  const setVerticalLayout = useCallback(() => {
    if (!frameRef.current?.contentWindow) return;
    frameRef.current.contentWindow.postMessage(
      {
        type: "SET_LAYOUT_DIRECTION",
        payload: "vertical",
      },
      "*",
    );
  }, []);

  // 更新iframe中的简历数据
  const updateResumeInFrame = useCallback(() => {
    if (!frameRef.current?.contentWindow) return;

    // 先设置垂直布局方向
    setVerticalLayout();

    // 然后发送简历数据
    const message = { type: "SET_RESUME", payload: resume.data };
    frameRef.current.contentWindow.postMessage(message, "*");
  }, [resume.data, setVerticalLayout]);

  // 监听iframe加载完成事件
  useEffect(() => {
    if (!frameRef.current) return;

    const onLoad = () => {
      // iframe加载完成后，先设置垂直布局，再发送简历数据
      setVerticalLayout();
      setTimeout(updateResumeInFrame, 100);

      // 手动调整iframe高度以显示全部内容
      setTimeout(() => {
        if (
          frameRef.current &&
          frameRef.current.contentDocument &&
          frameRef.current.contentDocument.body
        ) {
          const body = frameRef.current.contentDocument.body;
          const height = Math.max(body.scrollHeight, body.offsetHeight, 1000); // 确保至少有1000px高度
          frameRef.current.style.height = `${height}px`;

          const frameContainer = frameRef.current.parentElement;
          if (frameContainer) {
            frameContainer.style.minHeight = `${height * scale}px`;
          }
        }

        // 计算初始缩放
        calculateScale();
      }, 500);

      // 将iframe引用存储到全局状态中，以便工具栏可以访问
      useBuilderStore.getState().frame.setRef(frameRef.current);
    };

    frameRef.current.addEventListener("load", onLoad);
    return () => frameRef.current?.removeEventListener("load", onLoad);
  }, [updateResumeInFrame, setVerticalLayout, calculateScale, scale]);

  // 监听简历数据变化，实时更新预览
  useEffect(() => {
    // 当resume.data发生变化时，立即更新iframe中的内容
    updateResumeInFrame();
  }, [resume.data, updateResumeInFrame]);

  // 监听iframe大小变化消息
  useEffect(() => {
    if (!frameRef.current?.contentWindow) return;

    const handleMessage = (event: MessageEvent) => {
      if (!frameRef.current?.contentWindow) return;
      if (event.origin !== window.location.origin) return;

      switch (event.data.type) {
        case "PAGE_LOADED": {
          // 使用实际高度更新iframe高度，确保内容完全显示
          const height = event.data.payload.height || 1000; // 默认至少1000px高度
          frameRef.current.style.height = `${height}px`;

          // 对于长页面，确保父容器高度也合理设置
          const frameContainer = frameRef.current.parentElement;
          if (frameContainer) {
            // 保持容器高度略大于iframe高度，确保有足够空间显示
            frameContainer.style.minHeight = `${height * scale}px`;
          }

          // 页面加载完成后再次确认垂直布局和最新的简历数据
          setVerticalLayout();
          setTimeout(updateResumeInFrame, 100);

          // 重新计算缩放
          calculateScale();
          break;
        }
        case "ZOOM_IN": {
          handleZoomIn();
          break;
        }
        case "ZOOM_OUT": {
          handleZoomOut();
          break;
        }
        case "RESET_VIEW": {
          handleResetView();
          break;
        }
        case "CENTER_VIEW": {
          handleCenterView();
          break;
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [
    frameRef,
    updateResumeInFrame,
    setVerticalLayout,
    calculateScale,
    handleZoomIn,
    handleZoomOut,
    handleResetView,
    handleCenterView,
  ]);

  // 监听窗口大小变化，重新计算缩放
  useEffect(() => {
    const handleResize = debounce(() => {
      calculateScale();
    }, 200);

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      handleResize.cancel();
    };
  }, [calculateScale]);

  // 监听左侧面板状态变化，重新计算缩放
  useEffect(() => {
    // 面板大小或拖动状态变化时，延迟重新计算缩放
    const timeoutId = setTimeout(() => {
      calculateScale();
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [leftHandle.isDragging, calculateScale]);

  // 将缩放处理函数设置到全局状态中
  useEffect(() => {
    // 通过set方法更新，而不是直接赋值
    useBuilderStore.setState((state) => ({
      frame: {
        ...state.frame,
        handleZoomIn,
        handleZoomOut,
        handleResetView,
        handleCenterView,
      },
    }));
  }, [handleZoomIn, handleZoomOut, handleResetView, handleCenterView]);

  return (
    <div
      className="flex h-screen w-full flex-col"
      style={{
        transition: leftHandle.isDragging ? "none" : "width 0.2s ease-in-out",
      }}
    >
      <Helmet>
        <title>
          {title} - {t({ message: "NovaCV" })}
        </title>
      </Helmet>

      {/* 滚动容器 - 占满剩余高度并启用滚动 */}
      <div ref={containerRef} className="flex-1 overflow-y-auto pt-20">
        <div className="flex flex-col items-center">
          <div
            className="mx-auto overflow-visible rounded shadow-xl"
            style={{
              transformOrigin: "top center",
              margin: "0 auto",
              transform: `scale(${scale})`,
              transition: "transform 0.2s ease-in-out",
            }}
          >
            <iframe
              ref={frameRef}
              title={title}
              src="/artboard/builder"
              scrolling="no"
              style={{
                width: `${pageSizeMap[format].width}mm`,
                minHeight: `${pageSizeMap[format].height}mm`,
                border: "none",
                overflow: "visible",
              }}
            />
          </div>
          {/* 底部间距 */}
          <div className="h-24"></div>
        </div>
      </div>

      {/* Dify 聊天组件 */}
      <DifyChat />
    </div>
  );
};

export const builderLoader: LoaderFunction<ResumeDto> = async ({ params }) => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const id = params.id!;

    const resume = await queryClient.fetchQuery({
      queryKey: ["resume", { id }],
      queryFn: () => findResumeById({ id }),
    });

    useResumeStore.setState({ resume });
    useResumeStore.temporal.getState().clear();

    return resume;
  } catch {
    return redirect("/dashboard");
  }
};
